import { deepEqual } from 'fast-equals';
import type { Observable, OperatorFunction } from 'rxjs';
import { BehaviorSubject, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, map, take } from 'rxjs/operators';

import type {
  Addon,
  BillingPeriod,
  BillingPlan,
  CustomBillingPlan,
  PaymentInterval,
  PricingFeatureSubscription,
} from '@core/billing-types';
import { BillingPlanTier, Currency } from '@core/billing-types';
import type { CompanyVerificationRequestStatus } from '@core/spam-verification-types';
import { getProductCodename } from '@recruitee/billing-util';

import { findNextIntDivisibleBy } from '../../../helpers/find-next-divisible';
import { getActiveProductOrThrow } from '../../../helpers/get-active-product-or-throw';
import { getAssignedProduct } from '../../../helpers/get-assigned-product';
import { getProduct } from '../../../helpers/get-product';
import { getProductCodeOrThrow } from '../../../helpers/get-product-code-or-throw';
import { isPp24 } from '../../../helpers/is-pp24';
import { combineLatestProperties } from '../../../helpers/rx-operators';
import type { PricingFeatureLoadedModel } from '../../../types/pricing-feature.types';
import type {
  CustomizedSlots,
  PlanPickerState,
  PlanPickerStateFactoryData,
  RecognizedProductCode,
} from '../plan-picker.types';
import { getInitialChargebeeInterval } from './get-initial-interval';
import { planPickerProductFactory } from './plan-picker-product-factory';

type Model = PricingFeatureLoadedModel;

export const mapToProp = {
  billingPlans: (billingPlans: BillingPlan[]) => billingPlans,
  ownedSlots: (model: Model) =>
    Number.isFinite(model.slotsTotal) ? model.slotsTotal : model.jobSlotsUsed,
  isSelfService: (model: Model) =>
    !model.externalBilling &&
    (model.subscription ? model.subscription.autoCollection || !model.subscription.manual : true),
  isTrial: (model: Model) => !!model.trial,
  isExternalBilling: (model: Model) => model.externalBilling,
  isSubscriptionBlocked: (model: Model) => model.subscriptionStatus.blocked,
  isSubscriptionActive: (model: Model) => model.subscription?.active,
  hasAnySubscription: (model: Model) => !!model.subscription,
  activeProduct: (model: Model, billingPlans: BillingPlan[], activeBillingPlan: BillingPlan) =>
    getActiveProductOrThrow(model, billingPlans, activeBillingPlan),
  interval: (model: Model, activeBillingPlan?: BillingPlan) =>
    getInitialChargebeeInterval(<PricingFeatureSubscription>model.subscription, activeBillingPlan),
  intervalDirty: (model: Model, interval: PaymentInterval) =>
    model.subscription?.billingPeriod !== interval,
  slotsTotal: (model: Model) => model.slotsTotal,
  jobSlotsUsed: (model: Model) => model.jobSlotsUsed,
  assignedProduct: (model: Model, billingPlans: BillingPlan[], activeBillingPlan: BillingPlan) => {
    // old Plan Picker - we don't want to have a pp24 product here
    const assignedProduct = getAssignedProduct(model, billingPlans, activeBillingPlan);
    return assignedProduct && !isPp24(assignedProduct.tier) ? assignedProduct : undefined;
  },
  currency: (model: Model, assignedProduct?: BillingPlan) => {
    if (assignedProduct && assignedProduct?.currency !== model.currency && !model.currencyLock) {
      return Currency[assignedProduct?.currency];
    }

    return Currency[model.currency];
  },
  currencyLock: (model: Model) => model.currencyLock,
  subscriptionStatus: (model: Model) => model.subscription?.status || null,
  tier: (model: Model) => model.subscription?.tier,
  spamVerificationStatus: (model: Model) => model.spamVerificationStatus,
  addons: (addons: Addon[]) => addons,
} satisfies Record<keyof PlanPickerStateFactoryData, (...args: any[]) => any>;

export const createPlanPickerProduct = (
  codename: RecognizedProductCode,
  data: PlanPickerStateFactoryData,
  slots: CustomizedSlots,
  assignedProduct?: BillingPlan,
) => {
  const product = getProduct(codename, data.currency, data.billingPlans);
  return planPickerProductFactory(codename, data, slots[codename] || 0, product, assignedProduct);
};

const getHighlighted = (isTrial: boolean, activeCodename: BillingPlanTier): BillingPlanTier => {
  if (isTrial) {
    return BillingPlanTier.Scale;
  } else {
    switch (activeCodename) {
      case BillingPlanTier.Scale:
      case BillingPlanTier.Lead:
        return BillingPlanTier.Lead;

      default:
        return BillingPlanTier.Scale;
    }
  }
};

export const isCustomBillingPeriod = (billingPeriod: BillingPeriod) =>
  ![1, 12].includes(billingPeriod);

export const createPlanPickerState = (
  data: PlanPickerStateFactoryData,
  slots: CustomizedSlots,
): PlanPickerState => {
  const products = [
    createPlanPickerProduct(BillingPlanTier.Launch, data, slots, data.assignedProduct),
    createPlanPickerProduct(BillingPlanTier.Scale, data, slots, data.assignedProduct),
    createPlanPickerProduct(BillingPlanTier.Lead, data, slots, data.assignedProduct),
  ];

  const assignedPricingProduct = data.assignedProduct
    ? products.find(p => p.product?.id === data.assignedProduct?.id)
    : undefined;
  const assignedProductSlots = assignedPricingProduct?.state.selectedSlots;
  const assignedProductLimitExceeded = assignedPricingProduct?.limits.exceeded;

  return {
    products,
    showCurrencyControl: data.isSelfService && !data.currencyLock && !data.assignedProduct,
    showIntervalControl: data.isSelfService && !isCustomBillingPeriod(<BillingPeriod>data.interval),
    highlighted: getHighlighted(
      data.isTrial,
      getProductCodeOrThrow(getProductCodename(data.activeProduct)),
    ),
    assignedProduct: data.assignedProduct,
    assignedProductSlots,
    assignedProductLimitExceeded,
    isTrial: data.isTrial,
    isExternalBilling: data.isExternalBilling,
    isSelfService: data.isSelfService,
    currency: data.currency,
    interval: data.interval,
    activeProduct: data.activeProduct,
    ownedSlots: data.ownedSlots,
    tier: data.tier,
  };
};

const getInitialSlotOption = (
  codename: RecognizedProductCode,
  { activeProduct, slotsTotal, billingPlans, currency, jobSlotsUsed }: PlanPickerStateFactoryData,
): number => {
  const product = getProduct(codename, currency, billingPlans);

  if (Number.isFinite(slotsTotal)) {
    if (product) {
      if (activeProduct.id === product.id) {
        return slotsTotal;
      }

      return findNextIntDivisibleBy(Math.max(product.baseSlots, slotsTotal), 10);
    }

    return 0;
  } else {
    return product ? findNextIntDivisibleBy(Math.max(product.baseSlots, jobSlotsUsed), 10) : 0;
  }
};

const toState = (
  changeSlots$: Observable<{ codename: BillingPlanTier; value: number }>,
  slotsState$: BehaviorSubject<Record<RecognizedProductCode, number | undefined>>,
): OperatorFunction<PlanPickerStateFactoryData, PlanPickerState> => {
  changeSlots$.subscribe(change =>
    slotsState$.next({
      ...slotsState$.getValue(),
      [change.codename]: change.value,
    }),
  );

  return (source: Observable<PlanPickerStateFactoryData>) => {
    source
      .pipe(
        map(data => ({
          [BillingPlanTier.Launch]: getInitialSlotOption(BillingPlanTier.Launch, data),
          [BillingPlanTier.Scale]: getInitialSlotOption(BillingPlanTier.Scale, data),
          [BillingPlanTier.Lead]: getInitialSlotOption(BillingPlanTier.Lead, data),
        })),
        take(1),
      )
      .subscribe(initial => slotsState$.next(initial));

    return combineLatest([source, slotsState$.asObservable()]).pipe(
      map(([data, slots]) => createPlanPickerState(data, slots)),
    );
  };
};

// eslint-disable-next-line max-lines-per-function
export const planPicker = (
  model$: Observable<PricingFeatureLoadedModel>,
  changeInterval$: Observable<PaymentInterval>,
  changeCurrency$: Observable<Currency>,
  changeSlots$: Observable<{ codename: BillingPlanTier; value: number }>,
  billingPlans$: Observable<BillingPlan[] | null>,
  activeBillingPlan$: Observable<BillingPlan | undefined>,
  spamVerificationStatus$: Observable<CompanyVerificationRequestStatus>,
  addons$: Observable<Addon[]>,
  // eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
): Observable<PlanPickerState> => {
  const isSelfService$: Observable<boolean> = model$.pipe(
    map(mapToProp.isSelfService),
    distinctUntilChanged(),
  );

  const currencyLock$: Observable<boolean> = model$.pipe(map(mapToProp.currencyLock));

  const activeProduct$: Observable<BillingPlan> = combineLatest([
    model$,
    billingPlans$,
    activeBillingPlan$,
  ]).pipe(
    map(([model, billingPlans, activeBillingPlan]) =>
      mapToProp.activeProduct(model, billingPlans!, activeBillingPlan!),
    ),
    distinctUntilKeyChanged('id'),
  );

  const assignedProduct$: Observable<CustomBillingPlan | undefined> = combineLatest([
    model$,
    billingPlans$,
    activeBillingPlan$,
  ]).pipe(
    map(([model, billingPlans, activeBillingPlan]) =>
      mapToProp.assignedProduct(model, billingPlans!, activeBillingPlan!),
    ),
    distinctUntilChanged(deepEqual),
  );

  const interval$: Observable<PaymentInterval> = merge(
    combineLatest([model$, assignedProduct$]).pipe(
      map(([model, assignedProduct$]) => mapToProp.interval(model, assignedProduct$)),
    ),
    changeInterval$,
  ).pipe(distinctUntilChanged());

  const intervalDirty$: Observable<boolean> = combineLatest([model$, interval$]).pipe(
    map(args => mapToProp.intervalDirty(...args)),
  );

  const isTrial$: Observable<boolean> = model$.pipe(map(mapToProp.isTrial), distinctUntilChanged());
  const isExternalBilling$: Observable<boolean> = model$.pipe(
    map(mapToProp.isExternalBilling),
    distinctUntilChanged(),
  );

  const hasAnySubscription$: Observable<boolean> = model$.pipe(
    map(mapToProp.hasAnySubscription),
    distinctUntilChanged(),
  );

  const slotsTotal$ = model$.pipe(map(mapToProp.slotsTotal), distinctUntilChanged());
  const jobSlotsUsed$ = model$.pipe(map(mapToProp.jobSlotsUsed), distinctUntilChanged());

  const tier$ = model$.pipe(map(mapToProp.tier), distinctUntilChanged());

  const selectedCurrency$: Observable<Currency> = merge(
    combineLatest([model$, assignedProduct$]).pipe(map(args => mapToProp.currency(...args))),
    changeCurrency$,
  ).pipe(distinctUntilChanged());

  const ownedSlots$ = model$.pipe(map(mapToProp.ownedSlots));
  const isSubscriptionBlocked$ = model$.pipe(map(mapToProp.isSubscriptionBlocked));
  const isSubscriptionActive$ = model$.pipe(map(mapToProp.isSubscriptionActive));
  const subscriptionStatus$ = model$.pipe(map(mapToProp.subscriptionStatus));

  return combineLatestProperties({
    isSelfService: isSelfService$,
    isSubscriptionBlocked: isSubscriptionBlocked$,
    isSubscriptionActive: isSubscriptionActive$,
    interval: interval$,
    intervalDirty: intervalDirty$,
    currency: selectedCurrency$,
    currencyLock: currencyLock$,
    activeProduct: activeProduct$,
    assignedProduct: assignedProduct$,

    isTrial: isTrial$,
    isExternalBilling: isExternalBilling$,
    hasAnySubscription: hasAnySubscription$,
    slotsTotal: slotsTotal$,
    jobSlotsUsed: jobSlotsUsed$,
    ownedSlots: ownedSlots$,
    subscriptionStatus: subscriptionStatus$,
    billingPlans: billingPlans$,
    tier: tier$,
    spamVerificationStatus: spamVerificationStatus$,
    addons: addons$,
  }).pipe(
    toState(
      changeSlots$,
      new BehaviorSubject({
        [BillingPlanTier.Launch]: undefined,
        [BillingPlanTier.Scale]: undefined,
        [BillingPlanTier.Lead]: undefined,
      }),
    ),
  );
};
